import { AlertTriangle, Clock, Filter, Plus, Search, Settings, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useShiftTemplatesQuery } from "@/domains/shifts/hooks/use-shift-queries";
import { useWorkersQuery } from "@/domains/workers/hooks";
import type { Worker } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { CreateWorkerPlanDialog } from "./dialogs/create-worker-plan-dialog";
import { WorkerPlanCard } from "./worker-plan-card";
import { WorkersTimelineOverview } from "./workers-timeline-overview";

type PlanTypeFilter = "all" | "template" | "custom" | "unassigned";

export function WorkerPlansTab() {
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const [searchQuery, setSearchQuery] = useState("");
  const [planTypeFilter, setPlanTypeFilter] = useState<PlanTypeFilter>("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedWorkerSalonId, setSelectedWorkerSalonId] = useState<string | null>(null);

  // Fetch workers data
  const {
    data: workers,
    loading: workersLoading,
    error: workersError,
  } = useWorkersQuery({
    salonId: currentSalon?.id,
  });

  // Fetch templates for reference
  const { templates } = useShiftTemplatesQuery({
    skip: !currentSalon?.id,
  });

  const workersArray = workers?.workers?.workers;

  // Filter and search workers
  const filteredWorkers = useMemo(() => {
    if (!workersArray || !Array.isArray(workersArray)) return [];

    return workersArray.filter((worker) => {
      // Search filter
      const searchTerm = searchQuery.toLowerCase();
      const fullName =
        `${worker.user?.firstName || ""} ${worker.user?.lastName || ""}`.toLowerCase();
      const email = worker.user?.email?.toLowerCase() || "";

      const matchesSearch = fullName.includes(searchTerm) || email.includes(searchTerm);

      if (!matchesSearch) return false;

      // Plan type filter
      // Note: This would require adding plan data to worker query or separate query
      // For now, we'll show all workers and handle plan status in the card
      return true;
    });
  }, [workersArray, searchQuery]);

  const handleCreatePlan = (workerSalonId: string) => {
    setSelectedWorkerSalonId(workerSalonId);
    setIsCreateDialogOpen(true);
  };

  // Only block on the first load. A background refetch must not unmount the tree:
  // that would tear down any open dialog and discard what the user is typing in it.
  if (workersLoading && !workersArray) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted animate-pulse rounded" />
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (workersError) {
    return (
      <Card className="border-destructive/20 bg-destructive/5">
        <CardHeader>
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-5 h-5" />
            <CardTitle>Error Loading Workers</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Unable to load worker data. Please try again.
          </p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Worker Plans</h2>
          <p className="text-sm text-muted-foreground">
            Assign shift templates to workers or create custom schedules
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {filteredWorkers.length} workers
          </Badge>
        </div>
      </div>

      {/* Weekly Timeline Overview */}
      <WorkersTimelineOverview
        workers={filteredWorkers
          .map((worker) => {
            // Find worker salon ID for current salon
            const workerSalon = worker.workerSalons?.find((ws) => ws.salonId === currentSalon?.id);
            const workerSalonId = workerSalon?.id;

            return {
              worker: worker as Worker,
              workerSalonId: workerSalonId || "",
              plan: null, // Individual components will fetch their own plans
            };
          })
          .filter((item) => item.workerSalonId)}
      />

      {/* Four-Layer System Info */}
      <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-blue-900 dark:text-blue-100 text-base">
            Layer 2: Worker Plan Assignment
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                <Clock className="w-4 h-4" />
                <span className="font-medium">Template Mode</span>
              </div>
              <p className="text-blue-700 dark:text-blue-300 text-xs ml-6">
                Assign existing templates to workers. Changes to templates automatically update all
                assigned workers.
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200">
                <Settings className="w-4 h-4" />
                <span className="font-medium">Custom Mode</span>
              </div>
              <p className="text-blue-700 dark:text-blue-300 text-xs ml-6">
                Create unique schedules for specific workers. Independent of templates.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search workers by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <Select
            value={planTypeFilter}
            onValueChange={(value) => setPlanTypeFilter(value as PlanTypeFilter)}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Workers</SelectItem>
              <SelectItem value="template">Template Plans</SelectItem>
              <SelectItem value="custom">Custom Plans</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Workers List */}
      {filteredWorkers.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Workers Found</h3>
            <p className="text-sm text-muted-foreground text-center mb-4">
              {searchQuery
                ? "No workers match your search criteria. Try adjusting your search terms."
                : "No workers have been added to this salon yet."}
            </p>
            {!searchQuery && (
              <Button variant="outline" onClick={() => {}}>
                <Plus className="w-4 h-4 mr-2" />
                Add Workers
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredWorkers.map((worker) => {
            // Find worker salon ID for current salon
            const workerSalon = worker.workerSalons?.find((ws) => ws.salonId === currentSalon?.id);
            const workerSalonId = workerSalon?.id;

            return (
              <WorkerPlanCard
                key={worker.id}
                worker={worker as Worker}
                workerSalonId={workerSalonId}
                onCreatePlan={handleCreatePlan}
                templates={templates}
              />
            );
          })}
        </div>
      )}

      {/* Templates Summary */}
      {templates.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Available Templates</CardTitle>
            <CardDescription>Templates that can be assigned to workers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.slice(0, 6).map((template) => (
                <div
                  key={template.id}
                  className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                >
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: template.color || "#6b7280" }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{template.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {template.days.length} days • {template.timeIntervals?.length || 0} intervals
                    </p>
                  </div>
                </div>
              ))}
              {templates.length > 6 && (
                <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg text-muted-foreground">
                  <div className="w-3 h-3 rounded border border-current flex-shrink-0" />
                  <span className="text-sm">+{templates.length - 6} more</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Worker Plan Dialog */}
      <CreateWorkerPlanDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        workerSalonId={selectedWorkerSalonId}
        templates={templates}
      />
    </div>
  );
}
